<template>
  <div class="flex flex-col gap-2">
    <AutoComplete
      ref="autocompleteRef"
      v-model="innerModelValue"
      option-label="unrestricted_value"
      :delay="400"
      :min-length="3"
      :suggestions="suggestions"
      :input-id="id"
      force-selection
      :class="['peer shadow-outline w-full', $attrs.class]"
      input-class="!leading-4 !h-10 !bg-white !py-3 !px-2.5 !focus: outline-theme-yellow !w-full !text-[#000]"
      :placeholder="placeholder"
      :select-on-focus="false"
      :disabled="props.disabled"
      :readonly="props.readonly"
      @complete="updateQueryParams"
      @change="onChange"
      @focus="onFocus"
      @blur="onBlur"
      @option-select="innerModelValue = $event.value"
    >
      <template #option="{ option }">
        {{ option?.unrestricted_value }}
      </template>
    </AutoComplete>
    <BaseErrorMessage v-if="errMsg">{{ errMsg }}</BaseErrorMessage>
  </div>
</template>
<script lang="ts">
type AutoCompleteChangeEvent = {
  originalEvent: Event;
  value: unknown;
};
type AutoCompleteCompleteEvent = {
  originalEvent: Event;
  query: string;
};
type Props = {
  id?: string;
  placeholder?: string;
  modelValue?: string | Record<string, string>;
  disabled?: boolean;
  readonly?: boolean;
  errMsg?: string;
};
type Emits = {
  'update:modelValue': [value: string | object | null | undefined];
  blur: [e: Event];
  focus: [e: Event];
  change: [e: Event];
};
</script>
<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';

import AutoComplete from 'primevue/autocomplete';

import { debounce } from '@/shared/functions/debounce';
import { fetchDadataAddressByQuery, useDadataAddress } from '@/shared/composables/useDadata';
import { isDadataAddressDataEmpty } from '@/shared/lib/dadataAddressHelpers';
import type { IDadataAddress } from '@/shared/model/dadata';
import BaseErrorMessage from '@/shared/components-2/base/BaseErrorMessage.vue';

const props = withDefaults(defineProps<Props>(), { id: () => useId() });
const emit = defineEmits<Emits>();

const query = ref('');
const queryEnabled = ref(false);

const updateQueryParams = debounce(({ query: value }: AutoCompleteCompleteEvent) => {
  query.value = value;
  queryEnabled.value = value.length > 2;
}, 1000);

const { suggestions: _suggestions } = useDadataAddress({
  searchQuery: query,
  division: 'administrative',
  queryEnabled
});

const onFocus = (e: Event) => emit('focus', e);
const onBlur = (e: Event) => emit('blur', e);

const onChange = (e: AutoCompleteChangeEvent) => {
  emit('change', e.originalEvent);
};

const suggestions = ref([]);
watch(_suggestions, to => {
  suggestions.value = to;
});

const innerModelValue = computed({
  set: v => emit('update:modelValue', v),
  get: () => props.modelValue
});

const enrichInProgress = ref(false);

const getAddressText = (value: unknown): string => {
  if (value == null || typeof value !== 'object') return '';

  const addr = value as IDadataAddress;
  return String(addr.unrestricted_value ?? addr.value ?? '').trim();
};

const tryEnrichAddress = async (value: unknown) => {
  if (enrichInProgress.value || props.disabled || props.readonly) return;

  const text = getAddressText(value);
  if (!text || !isDadataAddressDataEmpty(value as IDadataAddress)) return;

  enrichInProgress.value = true;
  try {
    const suggestion = await fetchDadataAddressByQuery(text);
    if (suggestion) emit('update:modelValue', suggestion);
  } finally {
    enrichInProgress.value = false;
  }
};

watch(() => props.modelValue, tryEnrichAddress, { immediate: true });
</script>
